import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { AlertSuccess } from "../../utils/componentes-utiles/AlertError";
import { getEventos, getRopa, getTrabajadores } from "../../services/apis";
import { CrearEventoDialog } from "./CrearEventoDialog";
import { EVENT_TYPE_LIST, formatoFecha, getEventType } from "./eventosUtils";
import { EventoActividadCard } from "./EventoActividadCard";
import { EventoComidaCard } from "./EventoComidaCard";
import { EventoInfoCard } from "./EventoInfoCard";
import { EventoLimpiezaCard } from "./EventoLimpiezaCard";

const renderEventoCard = (evento, opTrabajadores, onChange) => {
	switch (Number(evento.id_tipo_evento)) {
		case 1:
			return <EventoComidaCard evento={evento} opTrabajadores={opTrabajadores} onChange={onChange} />;
		case 2:
			return <EventoLimpiezaCard evento={evento} opTrabajadores={opTrabajadores} onChange={onChange} />;
		case 3:
			return <EventoActividadCard evento={evento} opTrabajadores={opTrabajadores} onChange={onChange} />;
		default:
			return <EventoInfoCard evento={evento} opTrabajadores={opTrabajadores} onChange={onChange} />;
	}
};

const parseFechaEvento = (fecha) => {
	if (!fecha) {
		return Number.MAX_SAFE_INTEGER;
	}

	const time = new Date(`${fecha}T00:00:00`).getTime();
	return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

const minutosDesdeHora = (hora) => {
	if (!hora) {
		return Number.MAX_SAFE_INTEGER;
	}

	const [hours = "99", minutes = "99"] = String(hora).slice(0, 5).split(":");
	return (Number(hours) * 60) + Number(minutes);
};

const ordenarPorHora = (a, b) => {
	const diffHora = minutosDesdeHora(a.hora_inicio) - minutosDesdeHora(b.hora_inicio);

	if (diffHora !== 0) {
		return diffHora;
	}

	return Number(a.id || 0) - Number(b.id || 0);
};

const getAnioEvento = (fecha) => {
	if (!fecha) {
		return null;
	}

	const anio = Number(String(fecha).slice(0, 4));
	return Number.isNaN(anio) ? null : anio;
};

export const Eventos = () => {
	const [eventos, setEventos] = useState([]);
	const [opTrabajadores, setOpTrabajadores] = useState([]);
	const [opRopa, setOpRopa] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [crearOpen, setCrearOpen] = useState(false);
	const [tipoFiltro, setTipoFiltro] = useState("todos");
	const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
	const [success, setSuccess] = useState(false);

	const cargarEventos = useCallback(async () => {
		try {
			const response = await getEventos();
			if (response?.success) {
				setEventos(response.data || []);
			}
		} catch (error) {
			console.error("Error en carregar esdeveniments:", error);
		} finally {
			setLoaded(true);
		}
	}, []);

	const cargarTrabajadores = useCallback(async () => {
		try {
			const response = await getTrabajadores();
			const trabajadores = (response?.data || []).map((item) => ({
				label: item.nombre,
				value: item.id,
			}));
			setOpTrabajadores(trabajadores);
		} catch (error) {
			console.error("Error en carregar usuaris", error);
		}
	}, []);

	const cargarRopa = useCallback(async () => {
		try {
			const response = await getRopa();
			const prendas = (response?.data || []).map((item) => ({
				...item,
				label: item.nombre,
				value: item.id,
			}));
			setOpRopa(prendas);
		} catch (error) {
			console.error("Error en carregar roba", error);
		}
	}, []);

	useEffect(() => {
		cargarEventos();
		cargarTrabajadores();
		cargarRopa();
	}, [cargarEventos, cargarTrabajadores, cargarRopa]);

	useEffect(() => {
		if (success) {
			cargarEventos();
		}
	}, [success, cargarEventos]);

	const aniosDisponibles = useMemo(() => {
		const anios = new Set([new Date().getFullYear()]);

		eventos.forEach((evento) => {
			const anio = getAnioEvento(evento.fecha);
			if (anio) {
				anios.add(anio);
			}
		});

		return [...anios].sort((a, b) => b - a);
	}, [eventos]);

	const eventosDelAnio = useMemo(() => (
		eventos.filter((evento) => getAnioEvento(evento.fecha) === Number(anioSeleccionado))
	), [eventos, anioSeleccionado]);

	const eventosFiltrados = useMemo(() => {
		const hoy = new Date();
		hoy.setHours(0, 0, 0, 0);
		const hoyTime = hoy.getTime();

		const eventosOrdenados = [...eventosDelAnio].sort((a, b) => {
			const fechaA = parseFechaEvento(a.fecha);
			const fechaB = parseFechaEvento(b.fecha);
			const aPasado = fechaA < hoyTime;
			const bPasado = fechaB < hoyTime;

			if (aPasado !== bPasado) {
				return aPasado ? 1 : -1;
			}

			if (fechaA !== fechaB) {
				return aPasado ? fechaB - fechaA : fechaA - fechaB;
			}

			return ordenarPorHora(a, b);
		});

		if (tipoFiltro === "todos") {
			return eventosOrdenados;
		}

		return eventosOrdenados.filter((evento) => Number(evento.id_tipo_evento) === Number(tipoFiltro));
	}, [eventosDelAnio, tipoFiltro]);

	const eventosPorFecha = useMemo(() => (
		eventosFiltrados.reduce((acc, evento) => {
			const fecha = evento.fecha || "sin-fecha";
			if (!acc[fecha]) acc[fecha] = [];
			acc[fecha].push(evento);
			return acc;
		}, {})
	), [eventosFiltrados]);

	const contadorPorTipo = useMemo(() => (
		eventosDelAnio.reduce((acc, evento) => {
			const idTipo = Number(evento.id_tipo_evento);
			acc[idTipo] = (acc[idTipo] || 0) + 1;
			return acc;
		}, {})
	), [eventosDelAnio]);

	const handleEventoCreado = () => {
		setSuccess(true);
		cargarEventos();
	};

	return (
		<Box className="events-shell">
			<Paper elevation={0} className="events-header">
				<Box className="events-header-copy">
					<Typography className="events-eyebrow">Esdeveniments</Typography>
					<Typography variant="h4" className="events-title">
						Agenda d'esdeveniments
					</Typography>
				</Box>
				<Stack direction="row" className="events-header-actions">
					<TextField
						select
						label="Any"
						value={anioSeleccionado}
						onChange={(event) => setAnioSeleccionado(Number(event.target.value))}
						size="small"
						className="events-year-select"
					>
						{aniosDisponibles.map((anio) => (
							<MenuItem key={anio} value={anio}>
								{anio}
							</MenuItem>
						))}
					</TextField>
					<Chip icon={<EventAvailableIcon />} label={`${eventosDelAnio.length} esdeveniments`} className="events-stat-chip" />
					<Button variant="contained" startIcon={<AddIcon />} className="events-add-button" onClick={() => setCrearOpen(true)}>
						Afegir
					</Button>
				</Stack>
			</Paper>

			<Paper elevation={0} className="events-filter-bar">
				<Stack direction="row" className="events-filter-title">
					<FilterAltIcon />
					<Typography variant="h6">Filtrar per tipus</Typography>
				</Stack>
				<ToggleButtonGroup
					value={tipoFiltro}
					exclusive
					onChange={(event, value) => value !== null && setTipoFiltro(value)}
					className="events-type-filter"
				>
					<ToggleButton value="todos" className="events-filter-option events-filter-all">
						Tots
					</ToggleButton>
					{EVENT_TYPE_LIST.map((tipo) => {
						const TipoIcon = tipo.icon;
						return (
							<ToggleButton key={tipo.id} value={tipo.id} className={`events-filter-option ${tipo.className}`}>
								<TipoIcon fontSize="small" />
								{tipo.label}
								<span>{contadorPorTipo[tipo.id] || 0}</span>
							</ToggleButton>
						);
					})}
				</ToggleButtonGroup>
			</Paper>

			{!loaded && (
				<Box className="events-loading">
					<CircularProgress />
				</Box>
			)}

			{loaded && (
				<Stack className="events-day-list">
					{Object.entries(eventosPorFecha).map(([fecha, eventosDia]) => {
						const eventosDiaOrdenados = [...eventosDia].sort(ordenarPorHora);

						return (
							<section key={fecha} className="events-day-section">
								<Box className="events-day-heading">
									<Box className="events-day-title-block">
										<EventAvailableIcon fontSize="small" />
										<Typography variant="h6" className="events-day-title">
											{formatoFecha(fecha)}
										</Typography>
									</Box>
									<Stack direction="row" className="events-day-types">
										{EVENT_TYPE_LIST
											.filter((tipo) => eventosDia.some((evento) => Number(evento.id_tipo_evento) === tipo.id))
											.map((tipo) => {
												const TipoIcon = tipo.icon;
												const count = eventosDia.filter((evento) => Number(evento.id_tipo_evento) === tipo.id).length;
												return (
													<span key={tipo.id} className={`events-day-type ${tipo.className}`}>
														<TipoIcon fontSize="inherit" />
														{tipo.label}: {count}
													</span>
												);
											})}
									</Stack>
								</Box>

								<Grid container spacing={2}>
									{eventosDiaOrdenados.map((evento) => {
										const tipo = getEventType(evento.id_tipo_evento);
										return (
											<Grid key={evento.id} size={{ xs: 12, md: 6, xl: 4 }}>
												<Box className={`events-type-ribbon ${tipo.className}`}>
													{renderEventoCard(evento, opTrabajadores, setSuccess)}
												</Box>
											</Grid>
										);
									})}
								</Grid>
							</section>
						);
					})}
				</Stack>
			)}

			<CrearEventoDialog
				open={crearOpen}
				onClose={() => setCrearOpen(false)}
				opTrabajadores={opTrabajadores}
				opRopa={opRopa}
				onCreated={handleEventoCreado}
			/>

			{success && (
				<AlertSuccess open={success} setOpen={setSuccess} titulo="Èxit" mensaje="Canvis realitzats correctament" tipo="success" />
			)}
		</Box>
	);
};
